import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage           from './pages/LoginPage';
import InstructorDashboard from './pages/InstructorDashboard';
import StudentQueuePage    from './pages/StudentQueuePage';
import SetupPasswordPage   from './pages/SetupPasswordPage';
import ForgotPasswordPage  from './pages/ForgotPasswordPage';
import ResetPasswordPage   from './pages/ResetPasswordPage';
import RegisterInvitePage  from './pages/RegisterInvitePage';

const RegisterStudentPage = () => (
  <div style={{ padding: 40, color: 'var(--txt-dim)', textAlign: 'center', background: 'var(--bg)', minHeight: '100vh' }}>
    <p>Student registration — coming soon</p>
  </div>
);

// WCAG 2.4.2 (Page Titled). A single-page app keeps one <title> across every
// route unless something changes it, so a screen reader announces "Gradiance"
// whether you're signing in or sitting in a queue — and browser history and tab
// switching become guesswork. Kept in one place rather than a hook per page so
// routes I don't touch still get a sensible title.
const ROUTE_TITLES = {
  '/login':            'Sign in',
  '/student':          'Your queue',
  '/instructor':       'Instructor dashboard',
  '/register/student': 'Create your account',
  '/register/invite':  'Redeem an invite',
  '/setup-password':   'Set your password',
  '/forgot-password':  'Reset your password',
  '/reset-password':   'Choose a new password',
};

function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const page = ROUTE_TITLES[pathname];
    document.title = page ? `${page} · Gradiance` : 'Gradiance';
  }, [pathname]);
  return null;
}

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'STUDENT') return <Navigate to="/student" replace />;
  return <Navigate to="/instructor" replace />;
}

function RequireAuth({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <>
      <DocumentTitle />
      <Routes>
      <Route path="/"       element={<RootRedirect />} />
      <Route path="/login"  element={<LoginPage />} />

      <Route path="/instructor" element={
        <RequireAuth roles={['INSTRUCTOR', 'TA', 'ADMIN']}>
          <InstructorDashboard />
        </RequireAuth>
      } />

      <Route path="/student" element={
        <RequireAuth roles={['STUDENT']}>
          <StudentQueuePage />
        </RequireAuth>
      } />

      <Route path="/register/student" element={<RegisterStudentPage />} />
      <Route path="/register/invite"  element={<RegisterInvitePage />} />
      <Route path="/setup-password"   element={<SetupPasswordPage />} />
      <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
      <Route path="/reset-password"   element={<ResetPasswordPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
